import { FocusTrap, Button, Alert } from 'crux';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import at from 'lodash/at';
import { withTheme } from 'styled-components';
import { submitSectionQuestion, updateAnswersArray } from '../../../redux/actions/questions';
import Cover from '../../StyledComponents/MaskCover/Index';
import { getLoaders } from '../../../redux/selector/validations';
import { formatAddress } from '../../../util';
import {
  ModalWrapper,
  ModalHeading,
  ModalBody,
  QuestionBlock,
  QuestionWrapper,
  QuestionHeading,
  QuestionContent,
  Address,
  ButtonWrapper,
  Error,
  OuterCircle,
  InnerCircle,
  QuestionDescription,
} from './ModalStyles';
import { getCurrentQuestionSet } from '../../../redux/selector/questions';
import { getCSSVariables } from '../../../cruxComponents/utils';
// eslint-disable-next-line import/no-cycle
import QuestionRenderer from '../../QuestionRenderer';

const Modal = (props) => {
  const {
    errors,
    questionObj,
    handleModalButtonClick,
    enableContinue,
    isLoading,
    theme,
    showEditPageFlag,
    modalQuesitionList,
    sectionIndex,
    handleConfirmClick,
    uspsNextPage,
  } = props;
  const [response, setResponse] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(false);
  const { errorMessage, errorTitle, hasError } = errors;
  const {
    question_text: questionText,
    question_description: QuestionDesc,
    response_options: resOptions,
    send_individual_questions: sendIndividualQuestions,
    popup_button_text: popupButtonText,
  } = questionObj;

  const {
    config: {
      theme: {
        components: {
          card: { themeConfiguration: cardThemeConfiguration },
        },
      },
    },
  } = theme;
  const handleClick = () => {
    if (response === '') {
      setError(true);
    } else {
      setError(false);
      handleModalButtonClick({
        response,
        preventReflexiveCall: true,
        id: selectedId,
        showEditPageFlag,
        uspsNextPage,
      });
    }
  };

  useEffect(() => {
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, []);

  const getSelectedOption = (options, id) => {
    const selected = options.find((option) => option.id === id);
    const res = [];
    // this creates an array of selected address, city, zip code, state
    Object.values(selected).forEach((val) => {
      if (typeof val === 'object') {
        res.push(val);
      }
    });
    return res[0];
  };

  const selectedAddress = (id) => {
    setSelectedId(id);
    if (id !== '') {
      setError(false);
    }
    // Back end sends 'sendIndividualQuestions' param as true if
    // we need to send entire selected address, city, zip code rather than just
    // selected option's id which is the case with AllState,
    // other wise both the 'response' and the 'id' will be same
    if (!sendIndividualQuestions) {
      setResponse(id);
    } else {
      const selectedOption = getSelectedOption(questionObj.response_options, id);
      const updatedResponse = {
        ...selectedOption,
      };
      setResponse(updatedResponse);
    }
  };

  useEffect(() => {
    const prePopulatedResponseId = get(questionObj, 'response.id', '');
    if (prePopulatedResponseId) {
      selectedAddress(prePopulatedResponseId);
    }
  }, []);

  const colorOverride = get(theme, 'config.theme.global.colorOverride', null);
  const modalButtonTheme = get(theme, 'config.theme.global.modalButton.themeConfiguration', {});
  const commonBtnTheme = get(theme, 'config.theme.global.commonButtonTheme');
  const questionBlockTheme = get(theme, 'config.theme.global.modal.questionBlock');

  // eslint-disable-next-line react/destructuring-assignment
  const cssVariables = getCSSVariables(props.theme);
  const font = at(cssVariables, ['pageOverride.font', 'global.font']);
  modalButtonTheme.font = font?.[0]?.['button-modal'] || font?.[1]?.['button-modal'];
  const themeConfiguration = colorOverride
    ? {
        selectedBgColor: colorOverride.selectedBgColor,
        bgColor: '#ffffff',
        border: `1px solid ${colorOverride.selectedBgColor}`,
        fontColor: '#333333',
      }
    : { ...modalButtonTheme, ...commonBtnTheme };

  const getResponseOptions = () => {
    return resOptions.map(({ id, label, msg, suggested, actual }) => {
      return (
        <QuestionBlock
          id={ id }
          tabIndex='0'
          name='questions-block'
          selectedQuestion={ id === selectedId }
          onKeyDown={ (e) => e.key === 'Enter' && selectedAddress(id) }
          onClick={ () => selectedAddress(id) }
          questionBlockTheme={ questionBlockTheme }
        >
          <OuterCircle
            selected={ id === selectedId }
            tabIndex='-1'
            questionBlockTheme={ questionBlockTheme }
          >
            <InnerCircle tabIndex='-1' />
          </OuterCircle>
          <QuestionWrapper name='question-wrapper'>
            <QuestionHeading name='questions-heading'>{label}</QuestionHeading>
            <QuestionContent name='questions-content'>
              <Address name='address'>
                {id.toString().match(/[0-9]/g) || id.toString().includes('suggested')
                  ? msg || formatAddress(suggested, sendIndividualQuestions)
                  : formatAddress(actual, sendIndividualQuestions)}
              </Address>
            </QuestionContent>
          </QuestionWrapper>
        </QuestionBlock>
      );
    });
  };

  const getModalBody = () => {
    return isEmpty(resOptions) ? (
      <>
        <QuestionRenderer
          questionList={ modalQuesitionList }
          sectionIndex={ sectionIndex }
          showModalQuestions
        />
        <ButtonWrapper name='button-wrapper'>
          <Button
            name='confirm-button'
            label={ popupButtonText || 'Confirm' }
            type='filled'
            handleButtonClick={ (e) => {
              e.preventDefault();
              handleConfirmClick();
            } }
            width='90%'
            disabled={ !enableContinue }
            themeConfiguration={ themeConfiguration }
            alignment='center'
            tabIndex='0'
          />
        </ButtonWrapper>
      </>
    ) : (
      <>
        {getResponseOptions()}
        <ButtonWrapper name='button-wrapper' onClick={ handleClick }>
          <Button
            name='confirm-button'
            label={ popupButtonText || 'Confirm Address' }
            type='filled'
            handleButtonClick={ (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClick();
            } }
            width='90%'
            disabled={ !selectedId }
            themeConfiguration={ themeConfiguration }
            alignment='center'
            tabIndex='0'
          />
        </ButtonWrapper>
      </>
    );
  };

  return (
    <FocusTrap>
      <Cover name='cover' />
      <ModalWrapper
        name='modal-wrapper'
        isLoading={ isLoading }
        size={ isEmpty(resOptions) }
        cardThemeConfiguration={ cardThemeConfiguration }
      >
        {error ? <Error>Please select atleast one option!</Error> : null}
        {hasError && <Alert message={ errorMessage } type='error' title={ errorTitle } />}
        <ModalHeading name='modal-heading'>{questionText}</ModalHeading>
        <QuestionDescription error={ error }>{QuestionDesc}</QuestionDescription>
        <ModalBody name='modal-body'>{getModalBody()}</ModalBody>
      </ModalWrapper>
    </FocusTrap>
  );
};

function mapStateToProps(state, ownProps) {
  const { isLoading } = getLoaders(state);
  const { otherParams } = getCurrentQuestionSet(state);
  const showEditPageFlag = get(otherParams, 'show_edit_page_flag', false);
  const activeSubsectionId = get(state, 'questions.activeSubsectionId');
  const { sectionIndex } = ownProps;
  const { enableContinue, questionList } = get(
    state,
    `questions.sections.${activeSubsectionId}[${sectionIndex}]`,
    {
      enableContinue: true,
      questionList: [],
    },
  );
  const modalQuesitionList = questionList.filter((q) => q.isModalQuestion);
  const { actionMessage } = state.questions;
  let errors = { hasError: false };
  if (actionMessage && actionMessage.message && actionMessage.type !== 'success') {
    errors = {
      hasError: true,
      errorMessage: actionMessage.message,
    };
  }
  return {
    errors,
    isLoading,
    showEditPageFlag,
    enableContinue,
    modalQuesitionList,
    uspsNextPage: get(state, 'questions.uspsConfig.uspsNextPage'),
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    handleConfirmClick: (preventReflexiveCall = true) => {
      const questionId = get(ownProps, 'questionObj.question_id', '');
      const isHidden = !get(ownProps, 'questionObj.is_hidden', false);
      const auditRequired = get(ownProps, 'question.audit_required', false);
      dispatch(
        updateAnswersArray({
          questionId,
          response: '',
          sectionIndex: ownProps.sectionIndex,
          preventReflexiveCall,
          isHidden,
          auditRequired,
        }),
      );
      dispatch(submitSectionQuestion(ownProps.sectionIndex));
    },
    handleModalButtonClick: ({
      response,
      preventReflexiveCall,
      showEditPageFlag,
      uspsNextPage,
    }) => {
      let sectionIndex = 0;
      const auditRequired = get(ownProps, 'question.audit_required', false);
      const sendIndividualQuestions = get(ownProps, 'questionObj.send_individual_questions', false);
      if (sendIndividualQuestions) {
        Object.values(response).forEach((res) => {
          sectionIndex = res.sectionIndex;
          dispatch(
            updateAnswersArray({
              questionId: res.question_id,
              response: res.value,
              sectionIndex: res.sectionIndex,
              preventReflexiveCall,
              isControlledInput: true,
              isHidden: false,
              auditRequired,
            }),
          );
        });
      }
      if (showEditPageFlag) {
        dispatch(
          submitSectionQuestion(sectionIndex, { isReflexive: !uspsNextPage }, 'CONFIRM_UPDATE'),
        );
      } else {
        dispatch(submitSectionQuestion(sectionIndex, { isReflexive: !uspsNextPage }));
      }
    },
  };
}

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(Modal));
